<template>
  <Card :style="cardStyle" class="dark:opacity-95">
    <section class="flex items-center px-4 py-4">
      <div class="text-2xl leading-none">{{ formatTemp(currentTemperature, settingsStore.temperatureUnits) }}</div>
      <component :is="temperatureTrendIconComponent" class="text-xl"/>
      <div class="px-4 font-medium">{{ currentWeatherConditionsSummary }}</div>
    </section>
  </Card>
</template>

<script setup>
import {computed} from 'vue';
import Card from './Card.vue';
import {formatTemp} from '../utils/formatters.js';
import {getConditionMap} from '../utils/dataTransforms.js';
import {useI18n} from 'vue-i18n';
import {useSettingsStore} from '../stores/settings.js';
import {getHighContrastTextColor} from '../utils/colors.js';
import IconArrowWarmUp from '~icons/material-symbols/arrow-warm-up';
import IconArrowCoolDown from '~icons/material-symbols/arrow-cool-down';

const { t } = useI18n();
const settingsStore = useSettingsStore();
const conditionMap = computed(() => getConditionMap(t));

const props = defineProps({
  daily: Array,
});

// Sample the current time when forecast data changes, without a separate clock.
const currentForecast = computed(() => ({
  time: Math.ceil(Date.now() / 1000),
  tempData: props.daily.flatMap(day => day.tempData ?? []),
  skyData: props.daily.flatMap(day => day.skyData ?? []),
}));

const currentTemperature = computed(() => {
  const {tempData, time} = currentForecast.value;

  const temp1 = tempData.findLast(data => data.time <= time);
  const temp2 = tempData.find(data => data.time >= time);

  if (temp1.time === temp2.time) return temp1.temperature;

  return temp1.temperature +
      (temp2.temperature - temp1.temperature) *
      ((time - temp1.time) / (temp2.time - temp1.time));
});

const currentWeatherConditions = computed(() => {
  const {skyData, time} = currentForecast.value;

  const sky1 = skyData.findLast(data => data.time <= time);

  return sky1.conditions;
});

const currentWeatherConditionsSummary = computed(() => {
  const condition = conditionMap.value[currentWeatherConditions.value];
  return condition.label;
});

const temperatureTrendIconComponent = computed(() => {
  const {tempData, time} = currentForecast.value;
  const nextTempDataPoint = tempData.find(data => data.time > time);

  if (nextTempDataPoint) {
    if (nextTempDataPoint.temperature > currentTemperature.value) return IconArrowWarmUp;
    if (nextTempDataPoint.temperature < currentTemperature.value) return IconArrowCoolDown;
  }
  return null;
});

const cardStyle = computed(() => {
  const condition = conditionMap.value[currentWeatherConditions.value] ;
  const backgroundColor = condition.color;
  const textColor = getHighContrastTextColor(backgroundColor);
  return {
    backgroundColor,
    color: textColor,
  };
});
</script>
